import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import type { CloneDetail, EnvField } from '@api/api';
import { endpoints, qk } from '../../api/endpoints';
import { ErrorState, SkeletonRows } from '../../components/common';
import { EnvForm } from '../../components/forms';
import { LoadingButton } from '../../components/LoadingButton';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useNotify } from '../../components/Notify';
import { missingRequired } from '../../lib/validate';
import { useIsOwner } from '../../auth/AuthProvider';
import { useCloneAction } from './useCloneAction';

/** Only real changes: drop no-op edits and blank overrides of generated fields. */
export function envChanges(fields: EnvField[], edits: Record<string, string | null>): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const [k, v] of Object.entries(edits)) {
    const f = fields.find((x) => x.key === k);
    if (!f) continue;
    if (f.generated && !v) continue;
    if (v === f.value && f.value !== '••••') continue;
    out[k] = v === '' ? null : v;
  }
  return out;
}

export function KeysTab({ clone: c }: { clone: CloneDetail }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const owner = useIsOwner();
  const env = useQuery({ queryKey: qk.env(c.id), queryFn: () => endpoints.env(c.id) });
  const [edits, setEdits] = useState<Record<string, string | null>>({});
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [savedPending, setSavedPending] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const run = useCloneAction(c.id);
  const fields = env.data ?? [];
  const changes = useMemo(() => envChanges(fields, edits), [fields, edits]);
  const dirty = Object.keys(changes).length > 0;
  const missing = missingRequired(fields, edits, c.missingRequired);
  const live = c.status !== 'draft' && c.status !== 'provisioning';
  const canApply = c.actions?.env ?? live;

  const save = useMutation({
    mutationFn: () => endpoints.putEnv(c.id, changes),
    onSuccess: (f) => {
      qc.setQueryData(qk.env(c.id), f);
      void qc.invalidateQueries({ queryKey: qk.clone(c.id) });
      setEdits({});
      setRevealed({});
      if (live) setSavedPending(true);
      notify.ok(live ? 'Saved. Apply to the server to restart with the new keys.' : 'Saved');
    },
    onError: (e) => notify.error(e),
  });

  const reveal = async (key: string) => {
    try {
      const r = await endpoints.revealEnv(c.id, key);
      setRevealed((x) => ({ ...x, [key]: r.value }));
    } catch (e) {
      notify.error(e, 'Reveal failed');
    }
  };

  if (env.isLoading) return <SkeletonRows rows={6} height={56} />;
  if (env.isError) return <ErrorState error={env.error} onRetry={() => void env.refetch()} />;

  return (
    <Stack spacing={2} sx={{ pb: 10 }}>
      {missing.length > 0 && (
        <Alert severity="warning">
          <strong>{missing.length} required {missing.length === 1 ? 'key is' : 'keys are'} missing:</strong> {missing.join(', ')}
        </Alert>
      )}
      {savedPending && live && (
        <Alert
          severity="info"
          action={
            owner && (
              <Button color="inherit" size="small" onClick={() => setApplyOpen(true)}>
                Apply to server
              </Button>
            )
          }
        >
          Saved keys are not on the server yet. Apply rewrites .env and restarts the stack.
        </Alert>
      )}
      <EnvForm
        fields={fields}
        edits={edits}
        revealed={revealed}
        serverMissing={c.missingRequired}
        onReveal={owner ? reveal : undefined}
        onEdit={(k, v) =>
          setEdits((x) => {
            const n = { ...x };
            if (v === undefined) delete n[k];
            else n[k] = v;
            return n;
          })
        }
      />
      {owner && (
        <Paper elevation={6} sx={{ position: 'sticky', bottom: 16, p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', zIndex: 2 }}>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }} color={dirty ? 'text.primary' : 'text.secondary'}>
            {dirty ? `${Object.keys(changes).length} unsaved ${Object.keys(changes).length === 1 ? 'change' : 'changes'}` : 'No unsaved changes'}
          </Typography>
          {dirty && <Button onClick={() => setEdits({})}>Discard</Button>}
          <LoadingButton variant="contained" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </LoadingButton>
          {live && (
            <Box>
              <Button variant="outlined" startIcon={<CloudUploadOutlinedIcon />} disabled={dirty || !canApply} onClick={() => setApplyOpen(true)}>
                Apply to server
              </Button>
            </Box>
          )}
        </Paper>
      )}
      <ConfirmDialog
        open={applyOpen}
        title="Apply keys to the server?"
        text="Rewrites /srv/apps/.../.env from the saved keys and runs docker compose up -d --wait. Containers with changed variables restart."
        confirmLabel="Apply"
        busy={run.isPending}
        onClose={() => setApplyOpen(false)}
        onConfirm={() =>
          run.mutate(
            { action: 'env' },
            {
              onSuccess: () => setSavedPending(false),
              onSettled: () => setApplyOpen(false),
            },
          )
        }
      />
    </Stack>
  );
}
